import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { SidebarMenuButton, SidebarMenuItem } from "@loot-os/ui/components/sidebar";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, FlaskConical, Globe, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { setActiveEnvironment, useActiveEnvironmentId } from "../lib/environment";
import { useTRPC } from "../lib/trpc";

function CreateSandboxDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const trpc = useTRPC();
  const [name, setName] = useState("");

  const create = useMutation(
    trpc.environments.create.mutationOptions({
      onSuccess: (env) => {
        onOpenChange(false);
        setName("");
        // Jump straight into the new sandbox; the scope store resets the
        // query cache, which also refetches the environments list.
        setActiveEnvironment(env.id);
      },
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create sandbox</DialogTitle>
          <DialogDescription>
            A fresh, empty test environment — you'll switch to it right away.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ name });
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="header-sandbox-name">Name</Label>
            <Input
              id="header-sandbox-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Staging"
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? "Creating…" : "Create sandbox"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The environment row of the tenant sidebar, under Search and above the environment's pages:
 * which of the tenant's environments every page below reads, and a menu to switch. Amber when a
 * sandbox is active — the always-on replacement for the old sandbox banner. Picking live drops
 * the tenant's persisted entry (`scopeStore`); a sandbox is remembered per tenant.
 */
export function EnvironmentSwitcher({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  const storedEnvId = useActiveEnvironmentId();
  const envs = useQuery(trpc.environments.list.queryOptions());
  const active =
    envs.data?.find((e) => e.id === storedEnvId) ??
    envs.data?.find((e) => e.type === "live") ??
    null;

  // Reconcile a stale stored env id (e.g. sandbox deleted in another tab).
  useEffect(() => {
    if (storedEnvId && envs.data && !envs.data.some((e) => e.id === storedEnvId)) {
      setActiveEnvironment(null);
    }
  }, [storedEnvId, envs.data]);

  const switchEnv = (env: NonNullable<typeof envs.data>[number]) => {
    if (env.id === active?.id) return;
    // null = live: the server default, and it drops the tenant's persisted entry.
    setActiveEnvironment(env.type === "live" ? null : env.id);
  };

  const [showNewSandbox, setShowNewSandbox] = useState(false);
  const sandboxActive = active?.type === "sandbox";
  const label = active?.name ?? "Environment";

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <SidebarMenuButton
              tooltip={label}
              className={
                sandboxActive
                  ? "bg-amber-500/15 text-amber-600 hover:bg-amber-500/25 hover:text-amber-600 data-[popup-open]:bg-amber-500/25 data-[popup-open]:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300 dark:data-[popup-open]:text-amber-300"
                  : "data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              }
            />
          }
        >
          {sandboxActive ? <FlaskConical /> : <Globe />}
          <span className="truncate">{label}</span>
          {/* The visible label is the environment's name, so say what the button does. */}
          <span className="sr-only">Switch environment</span>
          <ChevronsUpDown className="ml-auto size-4 opacity-60 group-data-[collapsible=icon]:hidden" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={4} className="min-w-48">
          {(envs.data ?? []).map((env) => (
            <DropdownMenuItem key={env.id} onClick={() => switchEnv(env)}>
              {env.type === "sandbox" ? <FlaskConical className="text-amber-500" /> : <Globe />}
              <span className="truncate">{env.name}</span>
              {env.id === active?.id && <Check className="ml-auto size-4" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowNewSandbox(true)}>
            <Plus />
            New sandbox…
          </DropdownMenuItem>
          <DropdownMenuItem
            render={<Link to="/tenants/$tenantId/sandboxes" params={{ tenantId }} />}
          >
            Manage sandboxes…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateSandboxDialog open={showNewSandbox} onOpenChange={setShowNewSandbox} />
    </SidebarMenuItem>
  );
}
